/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useMemo, useEffect, useState } from 'react';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';

import Divider from '@material-ui/core/Divider';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { callApi } from 'api/helpers/api.helpers';
import { humanFileSize } from '../../shared/utils/stringUtils';
import draftApi from '../../api/draft.api';
import ZipIcon from '../icons/ZipIcon';

const useStyles = makeStyles(theme => ({
  externalContainer: {
    width: '17.875rem',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '.75rem',
  },
  packageIcon: {
    marginRight: '.5rem',
  },
  fileSize: {
    color: theme.palette.input,
  },
  downloadButton: {
    marginLeft: 'auto',
  },
}));

export default function ConfirmationPackageDownload({ className, submission }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [packageData, setPackageData] = useState(null);

  useEffect(() => {
    async function getPackageData() {
      const packageMetadata = await callApi(draftApi.getPackageMetadata)(submission.id);
      setPackageData(packageMetadata);
    }
    if (submission) {
      getPackageData();
    }
  }, [submission]);

  const downloadPackage = async () => {
    if (submission) {
      const partialUserReference = submission.basicFilingInfo.userReference.substring(0, 10);
      await callApi(draftApi.downloadPackageContent)(submission.id, `package_${partialUserReference}.zip`);
    }
  };

  const fileSize = useMemo(() => {
    if (packageData) {
      return humanFileSize(packageData.size);
    }
    return '';
  }, [packageData]);

  return (
    <div className={clsx(classes.externalContainer, className)}>
      <Divider />
      <div className={classes.container}>
        <ZipIcon className={classes.packageIcon} />
        <div>
          <Typography variant="body1">
            <strong>{t('submission.package')}</strong>
          </Typography>
          <Typography variant="body1" className={classes.fileSize}>
            <small>{fileSize}</small>
          </Typography>
        </div>
        <IconButton
          color="primary"
          className={classes.downloadButton}
          onClick={downloadPackage}
          disabled={packageData === null}
          data-testid="download-button"
        >
          <GetApp color={packageData ? 'primary' : 'disabled'} />
        </IconButton>
      </div>
    </div>
  );
}
